import React, { useEffect, useState, useCallback } from 'react';
import clausierService from '../services/clausierService';

/**
 * Bouton "Référentiel" placé dans le header mobile.
 * - Affiche l'âge du cache (jours/heures).
 * - Au clic : vide les deux caches (RapteckMobile + RapteckNormes), recharge
 *   le référentiel depuis l'API, puis pré-charge les templates par norme.
 *
 * Pourquoi : la PWA met en cache le référentiel (jusqu'à 30 jours pour les
 * templates par norme). Sans ce bouton, les modifications faites côté admin
 * sur le clausier ne sont pas visibles avant expiration du cache.
 */
export default function RefreshReferentielButton({ style = {}, className = 'refresh-referentiel-button' }) {
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const s = await clausierService.getCacheStats();
      setStats(s);
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleClick = async () => {
    if (busy) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      alert('⚠️ Hors-ligne — impossible de rafraîchir le référentiel.');
      return;
    }
    setBusy(true);
    try {
      await clausierService.clearCache();
      await clausierService.refresh();
      const fresh = await clausierService.getCacheStats();
      setStats(fresh);
      const total = (fresh?.templatesNormes || 0) + (fresh?.templatesArretes || 0);
      alert(`✅ Référentiel rafraîchi (${total} observations — ${fresh?.templatesNormes || 0} normes, ${fresh?.templatesArretes || 0} arrêtés).`);
    } catch (err) {
      console.error('❌ refresh clausier:', err);
      alert(`❌ Échec du rafraîchissement : ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const ageLabel = (() => {
    if (!stats?.exists) return 'cache vide';
    if (stats.expired) return 'cache expiré';
    if (stats.ageHours < 1) return '< 1h';
    if (stats.ageHours < 24) return `${stats.ageHours}h`;
    return `${stats.ageDays}j`;
  })();

  const title = stats?.exists
    ? `Cache : ${(stats.templatesNormes || 0) + (stats.templatesArretes || 0)} observations (${stats.normes || 0} normes + ${stats.arretes || 0} arrêtés) • âge ${ageLabel}${stats.expired ? ' (expiré)' : ''}`
    : 'Aucun cache local — cliquez pour charger';

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={busy}
      title={title}
      style={{
        background: busy
          ? '#94a3b8'
          : stats?.expired
            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        ...style,
      }}
    >
      <span>{busy ? '⏳' : '📚'}</span>
      <span>{busy ? 'Sync…' : 'Référentiel'}</span>
      {!busy && stats?.exists && (
        <span style={{ opacity: 0.8, fontSize: '11px', fontWeight: 500 }}>
          ({ageLabel})
        </span>
      )}
    </button>
  );
}
